import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { useMemberTaggingAlert } from '@/groups/coordinator/MemberTaggingAlertContext';
import { StackedAvatars } from '@/tables/StackedAvatars';
import WbsTable, { type Column } from '@/tables/WbsTable';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

type MemberTagItem = components['schemas']['MemberTagItem'];

/**
 * Legacy doesn't paginate tags, so one page is the whole set. The limit is
 * passed explicitly because the endpoint defaults to 10.
 */
const TAG_PAGE_SIZE = 50;

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/member-tagging/',
)({
  component: MemberTaggingScreen,
});

function MemberTaggingScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const { setFlash } = useMemberTaggingAlert();

  const { data: groupData, isPending: groupPending } =
    api.getGroupBySlug.useQuery({
      path: { group_slug: groupName },
    });
  const groupId = groupData?.id ?? 0;

  const { data, isFetching } = api.listGroupMemberTags.useQuery(
    { path: { group_id: groupId }, query: { limit: TAG_PAGE_SIZE } },
    { enabled: groupId > 0 },
  );
  const tags = data?.data ?? [];

  const { mutateAsync: deleteTag, error: deleteError } =
    api.deleteGroupMemberTag.useMutation();

  const handleDelete = async (tagId: number): Promise<void> => {
    // Legacy has no server-side confirmation either, only this JS confirm.
    if (!window.confirm(t('actions.are-you-sure-you-5'))) return;
    try {
      await deleteTag({ path: { group_id: groupId, tag_id: tagId } });
      await api.listGroupMemberTags.invalidateQueries();
      // The assignments cascade with the tag, so the Tags column on the
      // group's All Students list is stale too.
      await api.getGroupStudents.invalidateQueries();
      setFlash('deleted');
    } catch {
      // Rendered from `deleteError` below.
    }
  };

  const columns: Column<MemberTagItem>[] = [
    {
      key: 'label',
      title: t('wbs.tag-name'),
      sortable: true,
      width: '200px',
    },
    {
      key: 'member_preview',
      title: t('profile.members'),
      // Branch on the member count, not the preview, which holds at most three.
      render: (_value, row) =>
        row.member_count === 0 ? (
          <NoMembersText>{t('profile.no-members')}</NoMembersText>
        ) : (
          <StackedAvatars
            max={3}
            total={row.member_count}
            members={row.member_preview.map((m) => ({
              id: m.id,
              name: m.display_name,
              avatar: m.avatar || null,
            }))}
          />
        ),
      width: 'auto',
      cellStyle: { verticalAlign: 'middle', padding: '0.5rem 0.3rem' },
    },
    {
      key: 'id',
      title: t('wbs.actions'),
      width: '140px',
      render: (_value, row) => (
        <ActionsCell>
          <ActionLink
            type="button"
            onClick={() =>
              navigate({
                to: '/groups/$groupName/admin/member-tagging/edit/$tagId/',
                params: { groupName, tagId: String(row.id) },
              })
            }
          >
            {t('actions.edit')}
          </ActionLink>
          <span>|</span>
          <ActionLink
            type="button"
            onClick={() => {
              void handleDelete(row.id);
            }}
          >
            {t('actions.delete')}
          </ActionLink>
        </ActionsCell>
      ),
    },
  ];

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('profile.member-tagging')}
          placement="study_helper_group_settings"
        />
        <WBSButton
          wide
          onClick={() =>
            navigate({
              to: '/groups/$groupName/admin/member-tagging/add/',
              params: { groupName },
            })
          }
        >
          {t('actions.add-new-tag')}
        </WBSButton>
      </GroupHeaderWrapper>
      {deleteError && (
        <AlertBox variant="error">
          {errorMessage(deleteError, t('wbs.something-went-wrong-please'))}
        </AlertBox>
      )}
      {/* The tag query cannot start until the slug resolves to an id, so the
          group read counts as loading here too — otherwise a group that has
          tags flashes the "no tags" empty state on every first paint. */}
      <WbsTable
        data={tags}
        columns={columns}
        loading={groupPending || isFetching}
        emptyMessage={t('wbs.there-are-no-tags')}
      />
    </>
  );
}

const NoMembersText = styled.span`
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 13px;
  font-style: italic;
`;

const ActionsCell = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const ActionLink = styled.button`
  background: none;
  border: none;
  color: var(--wbs-red);
  cursor: pointer;
  font-family: var(--raleway);
  font-size: 13px;
  padding: 0;
  text-decoration: none;
`;
